import {
  showSuccess,
  showError,
  showConfirm,
  hideLoading,
} from "@/assets/js/utils/alertService"; // ✅ 引入 SweetAlert2 提示服務
import api from "@/assets/js/utils/api"; // ✅ 引入封裝後的 API 服務

import { DateHelper } from "@/assets/js/utils/time";
import FormatView from "@/components/FormatView.vue";

export default {
  name: "ProductManagement",
  components: {
    FormatView,
  },
  data() {
    return {
      products: [],
      categories: [], // ✅ 儲存從後端載入的子分類
      showProductModal: false,
      editingProduct: null,
      newTag: "", // ✅ 用於暫存輸入中的標籤
      searchQuery: "", // 🔍 搜尋關鍵字
      filterStatus: "", // 🧩 篩選狀態
      filterCategory: "", // 🧩 篩選類別
      currentPage: 1, // 頁碼控制
      itemsPerPage: 8, // 每頁顯示數量
      userRole: "", // ✅ 新增：用戶角色
      formData: {
        name: "",
        price: 0,
        costPrice: 0, // ✅ 新增：成本价格
        stock: 0,
        subCategoryId: 0, // ✅ 綁定後端的 SubCategoryId
        active: true,
        image: null, // 新增：儲存圖片檔案
        imagePreview: null, // 新增：預覽圖片 URL
        trackStock: true, // 改為預設開啟
        stockStatus: "inStock", // 新增：庫存狀態 ('inStock' 或 'noStock')
        tags: [], // ✅ 新增標籤欄位
        videoUrl: "", // ✅ 新增影片連結屬性
      },
      showFormatModal: false, // 控制規格彈窗顯示
      selectedProduct: null, // 暫存點擊的商品資料
      formatData: null, // 儲存 FormatView 回傳資料
      isSpecificationStep: false, // 是否進入規格設定階段
      createdProductId: null, // 暫存剛新增的商品 ID
    };
  },

  computed: {
    activeProducts() {
      return this.products.filter((p) => p.active).length; // ✅ 計算上架商品
    },

    filteredProducts() {
      return this.products.filter((p) => {
        // ✅ 安全判斷，避免 p.name 為 undefined 時報錯
        const productName = (p.name || "").toLowerCase();
        const search = (this.searchQuery || "").toLowerCase();
        const matchesSearch = productName.includes(search);

        // 篩選條件
        let matchesFilter = true;
        if (this.filterStatus === "active") {
          matchesFilter = p.active === true;
        } else if (this.filterStatus === "inactive") {
          // 👈 新增
          matchesFilter = p.active === false;
        } else if (this.filterStatus === "lowStock") {
          matchesFilter = p.stock > 0 && p.stock <= 10;
        } else if (this.filterStatus === "outOfStock") {
          matchesFilter = p.stock === 0;
        }

        return matchesSearch && matchesFilter;
      });
    },

    // 分頁邏輯
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProducts.slice(start, end);
    },

    // 計算總頁數（至少回傳 1）
    totalPages() {
      const pages = Math.ceil(this.filteredProducts.length / this.itemsPerPage);
      return pages > 0 ? pages : 1;
    },

    // ✅ 新增：判斷是否可以查看成本價格（只有平台總管和工程師）
    canViewCostPrice() {
      return this.userRole === "平台總管" || this.userRole === "工程師";
    },
  },

  // ✅ 加上這段 watch
  watch: {
    currentPage() {
      window.scrollTo({
        top: 0,
        behavior: "smooth", // 平滑滾動回頂部
      });
    },
    // 當篩選結果變少時，若 currentPage 超出範圍則修正
    filteredProducts() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },

    "formData.trackStock"(val) {
      this.formData.meter = val; // 同步 meter 給後端用

      if (!val) {
        // 🚫 當關閉庫存追蹤時
        // 若目前庫存已經有數量，則保留不改變
        if (this.formData.stock > 0) {
          // ✅ 保留原本庫存
        } else {
          // ✅ 若本來就沒庫存，則根據狀態設定
          if (this.formData.stockStatus === "noStock") {
            this.formData.stock = 0;
          } else {
            this.formData.stock = 1;
          }
        }
      }
    },

    // ✅ 當切換庫存狀態時（只有在未追蹤庫存時才自動控制 stock）
    "formData.stockStatus"(val) {
      if (!this.formData.trackStock) {
        if (val === "noStock") {
          this.formData.stock = 0;
        } else if (val === "inStock" && this.formData.stock === 0) {
          // 如果從無庫存切回有庫存時，預設為 1
          this.formData.stock = 1;
        }
      }
    },
  },

  methods: {
    // ✅ 新增：驗證用戶角色
    async verifyUserRole() {
      try {
        const res = await api.get("/back/admin/verify");
        if (res.data && res.data.data) {
          this.userRole = res.data.data.Role || "";
          console.log(`✅ 用戶角色: ${this.userRole}`);
        }
      } catch (error) {
        console.warn("⚠️ 無法獲取用戶角色:", error);
        this.userRole = "";
      }
    },

    // 分頁方法：上一頁 / 下一頁
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },

    formatDate(date) {
      return DateHelper.toTaiwanFormat(date, false);
    },
    async fetchProducts() {
      try {
        const res = await api.get("/product/list"); // ✅ 使用封裝後的 API

        if (res.data && res.data.success) {
          // ✅ 轉換後端資料結構為前端可用格式
          this.products = res.data.data.map((item) => ({
            id: item.Id, // 對應後端欄位
            name: item.ProductName,
            price: item.Price,
            costPrice: item.CostPrice || 0, // ✅ 新增：成本價格
            stock: item.Stock,
            subCategoryId: item.SubCategoryId,
            active: item.IsActive,
            imageUrl:
              item.PictureURL && item.PictureURL.trim() !== ""
                ? item.PictureURL
                : require("@/assets/image/AYH.png"),
            description: item.Description,
            tags: [item.Tag1, item.Tag2, item.Tag3].filter(Boolean), // 過濾掉 null / undefined
            videoUrl: item.VideoURL,
            meter: Boolean(Number(item.Meter)), // ✅ 這一行很重要
            createdBy: item.CreatedBy,
            createdDate: item.CreatedDate,
            hasVariant: item.HasVariant,
            variants: item.Variants || [],
          }));

          hideLoading(); // 關閉 loading
        } else {
          hideLoading();
          showError(res.data?.message || "資料載入失敗！");
        }
      } catch (error) {
        hideLoading();
        console.error("❌ API 請求錯誤：", error);
        showError("無法連線至伺服器，請確認後端 API 是否啟動！");
      }
    },

    async fetchCategories() {
      try {
        const res = await api.get("/category/all");

        if (res.data.success) {
          const subs = res.data.data.flatMap((cat) =>
            Array.isArray(cat.SubCategories) ? cat.SubCategories : [],
          );

          // ✅ 統一轉成簡單格式
          this.categories = subs.map((s) => ({
            id: Number(s.SubCategoryId),
            name: s.SubCategoryName || "未命名分類", // 防呆避免空字串
            isActive: s.IsActive,
          }));
          hideLoading();
        } else {
          hideLoading();
          showError("載入分類失敗！");
        }
      } catch (error) {
        hideLoading();
        console.error("分類載入錯誤:", error);
        showError("無法載入分類，請確認後端服務！");
      }
    },

    getStockClass(stock) {
      if (stock === 0) return "stock-out";
      if (stock <= 10) return "stock-low";
      return "stock-normal";
    },

    async openAddProductModal() {
      try {
        await this.fetchCategories(); // 🚀 重新取得最新分類資料
        hideLoading();

        this.editingProduct = null;
        this.isSpecificationStep = false; // ✅ 重置規格階段狀態
        this.createdProductId = null; // ✅ 清空暫存 ID

        this.formData = {
          name: "",
          price: 0,
          stock: 0,
          subCategoryId: 0,
          active: true,
          image: null,
          imagePreview: null,
          trackStock: false,
          meter: false,
          stockStatus: "inStock",
          tags: [],
          videoUrl: "",
          description: "",
        };

        this.showProductModal = true;
      } catch (error) {
        hideLoading();
        console.error("❌ 開啟新增商品失敗:", error);
        showError("無法載入分類,請稍後再試！");
      }
    },

    async openEditModal(product) {
      try {
        await this.fetchCategories(); // 🚀 重新取得最新分類資料
        hideLoading();

        this.editingProduct = product;

        this.formData = {
          name: product.name || "",
          price: product.price || 0,
          costPrice: product.costPrice || 0, // ✅ 新增：读取成本价格
          stock: product.stock || 0,
          subCategoryId: product.subCategoryId || "", // ✅ 帶入分類
          active: product.active ?? true,
          imagePreview: product.imageUrl || null,
          meter: product.meter ?? false, // ✅ 從後端帶入 meter
          trackStock: product.meter ?? false, // ✅ 與 meter 同步
          stockStatus: product.stock > 0 ? "inStock" : "noStock",
          tags: product.tags || [],
          videoUrl: product.videoUrl || "",
          description: product.description || "",
        };

        this.showProductModal = true; // ✅ 等分類載完後再開啟
      } catch (error) {
        hideLoading();
        console.error("❌ 開啟編輯商品失敗:", error);
        showError("無法載入分類，請稍後再試！");
      }
    },

    closeProductModal() {
      this.showProductModal = false;
      this.editingProduct = null;
      this.isSpecificationStep = false; // ← 新增
      this.createdProductId = null; // ← 新增
    },

    async saveProduct() {
      try {
        // 🔸 驗證必填欄位
        if (!this.formData.name || !this.formData.price) {
          hideLoading();
          showError("請完整填寫商品名稱與價格！");
          return;
        }

        // 🔸 驗證是否選擇分類（子類別）
        if (
          !this.formData.subCategoryId ||
          this.formData.subCategoryId === ""
        ) {
          hideLoading();
          showError("請選擇商品所屬分類！");
          return;
        }

        // 🔸 將標籤分別存入 Tag1、Tag2、Tag3
        const [Tag1, Tag2, Tag3] = this.formData.tags;

        // 🔸 組裝商品物件
        const productObj = {
          ProductName: this.formData.name,
          Description: this.formData.description || "",
          Price: this.formData.price,
          CostPrice: this.formData.costPrice || 0, // ✅ 新增：成本价格
          Meter: this.formData.meter,
          Stock: this.formData.stock || 0,
          SubCategoryId: this.formData.subCategoryId || null,
          VideoURL: this.formData.videoUrl || "",
          Tag1: Tag1 || "",
          Tag2: Tag2 || "",
          Tag3: Tag3 || "",
          IsActive: this.formData.active,
        };

        // 🔸 建立 FormData
        const formData = new FormData();
        formData.append("product", JSON.stringify(productObj));
        if (this.formData.image) {
          formData.append("file", this.formData.image);
        }

        // ✅ 判斷是「新增」還是「編輯」
        if (this.editingProduct) {
          // ✅ 編輯模式：呼叫更新 API
          const id = this.editingProduct.id;
          const res = await api.put(`/product/update/${id}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          if (res.data && res.data.success) {
            this.closeProductModal();
            showSuccess("✅ 商品更新成功！");
            await this.fetchProducts();
          } else {
            showError(res.data?.message || "更新商品失敗！");
          }
        } else {
          // ✅ 新增模式：呼叫新增 API
          const res = await api.post("/product/add", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          if (res.data && res.data.success) {
            // ✅ 先重新載入商品列表以取得最新的商品 ID
            await this.fetchProducts();

            // ✅ 找出剛新增的商品（根據名稱找最新的）
            const newProduct = this.products
              .filter((p) => p.name === this.formData.name)
              .sort(
                (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
              )[0];

            if (newProduct) {
              this.createdProductId = newProduct.id; // 暫存商品 ID
              this.isSpecificationStep = true; // 切換到規格階段

              // ✅ 開啟規格管理彈窗
              this.selectedProduct = {
                productId: this.createdProductId,
                productName: this.formData.name,
                hasVariant: false,
                variants: [],
                price: this.formData.price,
                stock: this.formData.stock,
                sku: "",
              };

              this.showFormatModal = true;
            } else {
              showError("無法取得新增的商品資訊！");
            }
          } else {
            showError(res.data?.message || "新增商品失敗！");
          }
        }
      } catch (error) {
        console.error("❌ 儲存商品錯誤：", error);
        showError("商品儲存失敗，請確認伺服器狀態！");
      } finally {
        hideLoading();
      }
    },

    async deleteProduct(id) {
      const product = this.products.find((p) => p.id === id);

      const confirmed = await showConfirm(
        `確定要刪除商品「${product.name}」嗎？此操作無法復原！`,
      );

      if (!confirmed.isConfirmed) return;

      try {
        // 🔹 呼叫後端刪除 API（軟刪除）
        const res = await api.delete(`/product/delete/${id}`);

        if (res.data && res.data.success) {
          showSuccess("✅ 商品已成功刪除！");
          // 重新載入商品資料
          await this.fetchProducts();
        } else {
          showError(res.data?.message || "刪除商品失敗！");
        }
      } catch (error) {
        console.error("❌ 刪除商品錯誤：", error);
        showError("刪除商品失敗，請確認伺服器狀態！");
      } finally {
        hideLoading();
      }
    },

    handleImageError(event) {
      console.warn("🖼️ 預覽圖片載入失敗，使用預設圖");
      event.target.src = require("@/assets/image/AYH.png"); // 預設圖 fallback
      event.target.onerror = null; // 避免死循環
    },

    // 處理圖片上傳
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 2 * 1024 * 1024) {
        showError("圖片檔案不能超過 2MB");
        return;
      }

      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        showError("只接受 JPG、JPEG、PNG 格式");
        return;
      }

      this.formData.image = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.formData.imagePreview = e.target.result;
        showSuccess("圖片上傳成功！");
      };
      reader.readAsDataURL(file);
    },

    removeImage() {
      this.formData.image = null;
      this.formData.imagePreview = null;
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = "";
      }
      showSuccess("已移除圖片！");
    },

    addTag() {
      const tag = this.newTag.trim().replace(/,/g, ""); // 移除空白與逗號

      // ✅ 驗證字數長度（允許 1~3 個字）
      if (tag.length === 0) {
        showError("請輸入標籤內容！");
        this.newTag = "";
        return;
      }
      if (tag.length > 3) {
        showError("標籤不可超過 3 個字！");
        this.newTag = "";
        return;
      }

      // ✅ 驗證上限
      if (this.formData.tags.length >= 3) {
        showError("最多只能新增 3 個標籤！");
        this.newTag = "";
        return;
      }

      // ✅ 驗證是否重複
      if (this.formData.tags.includes(tag)) {
        showError(`標籤「${tag}」已存在！`);
        this.newTag = "";
        return;
      }

      // ✅ 通過驗證才可新增
      this.formData.tags.push(tag);
      showSuccess(`已新增標籤：「${tag}」`);
      this.newTag = "";
    },

    limitTagLength() {
      if (this.newTag.length > 3) {
        this.newTag = this.newTag.slice(0, 3); // ✂️ 自動裁切多餘字元
        showError("標籤最多 3 個字！");
      }
    },

    removeTag(index) {
      const removed = this.formData.tags[index];
      this.formData.tags.splice(index, 1);
      showSuccess(`已移除標籤：「${removed}」`);
    },

    handleTagInput(event) {
      // 若輸入框為空或已被清空，就不做任何動作
      if (!this.newTag || this.newTag.trim() === "") return;

      if (event.key === "Enter" || event.key === ",") {
        this.addTag();
      }
    },

    getCategoryName(subCategoryId) {
      // 此段程式碼是控制：分類 ID 轉中文名稱
      const category = this.categories.find(
        (c) => c.id === Number(subCategoryId),
      );
      return category ? category.name : "未分類";
    },

    async exportReport() {
      try {
        const headers = [
          "商品名稱",
          "規格名稱",
          "商品貨號",
          "價格",
          "庫存",
          "分類",
          "上架狀態",
        ];

        const rows = [];

        this.products.forEach((product) => {
          const categoryName = this.getCategoryName(product.subCategoryId);

          // 🟢 多規格商品
          if (
            product.hasVariant &&
            Array.isArray(product.variants) &&
            product.variants.length > 0
          ) {
            product.variants.forEach((variant) => {
              rows.push([
                product.name, // 商品名稱
                variant.VariantName || "-", // 規格名稱
                variant.Sku || "-", // 商品貨號
                variant.Price ?? product.price ?? 0, // 價格
                variant.Stock ?? 0, // 庫存
                categoryName, // ✅ 中文分類
                product.active ? "上架" : "下架",
              ]);
            });
          }
          // 🟡 單一規格商品
          else {
            rows.push([
              product.name,
              "單一規格",
              product.sku || "-",
              product.price ?? 0,
              product.stock ?? 0,
              categoryName, // ✅ 中文分類
              product.active ? "上架" : "下架",
            ]);
          }
        });

        // 🔹 組 CSV（UTF-8 + BOM）
        let csvContent = headers.join(",") + "\n";
        csvContent += rows
          .map((row) =>
            row
              .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
              .join(","),
          )
          .join("\n");

        const blob = new Blob(["\uFEFF" + csvContent], {
          type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "商品規格報表.csv";
        link.click();

        showSuccess("📄 商品規格報表匯出成功！");
      } catch (error) {
        console.error("❌ 匯出報表錯誤:", error);
        showError("匯出報表失敗！");
      }
    },

    // 🔥 打開規格浮窗
    openSpecModal(product) {
      // 🟢 直接帶 HasVariant 與 Variants
      this.selectedProduct = {
        productId: product.id,
        productName: product.name,
        hasVariant: product.hasVariant === true, // ← 布林值
        variants: product.variants || [], // ← 後端的 Variant 陣列
        price: product.price || 0, // ← 單規格用
        stock: product.stock || 0, // ← 單規格用
        sku: product.sku || "", // ← 單規格用
      };

      this.showFormatModal = true; // 開啟規格視窗
    },

    // ⭐ 接收規格輸出的資料，呼叫後端 API 更新規格
    async submitVariants(result) {
      try {
        // 🚀 呼叫後端 API
        const res = await api.put("/product/variant/update", result);

        if (res.data && res.data.success) {
          showSuccess("規格已更新成功！");
          this.showFormatModal = false; // 關閉規格視窗

          // ✅ 如果是新增商品流程，關閉商品彈窗並顯示完成訊息
          if (this.isSpecificationStep) {
            this.closeProductModal();
            showSuccess("✅ 商品新增完成！");
          }

          await this.fetchProducts(); // 重新載入商品
        } else {
          showError(res.data?.message || "規格更新失敗！");
        }
      } catch (err) {
        console.error("❌ 更新規格錯誤:", err);
        showError("無法更新規格，請稍後再試！");
      }
    },

    // ✅ 新增：從規格管理返回商品編輯
    handleBackToProductEdit() {
      // 關閉規格彈窗，保持商品彈窗開啟
      this.showFormatModal = false;
      // 不需要做其他處理，formData 已經保留了原本的資料
      // showProductModal 保持 true 狀態
    },
  },

  async mounted() {
    // ✅ 新增：先驗證用戶權限
    await this.verifyUserRole();
    await Promise.all([this.fetchProducts(), this.fetchCategories()]);
  },
};
